<template>
  <div class="form-group">
    <div class="row">
      <div class="col-4">
        <div class="label align-items-start">
          <div>
            <div v-if="draggable" class="draggable">
              <ButtonElement is-draggable-button class="btn-large"></ButtonElement>
            </div>
            <div v-else class="notDraggable" style="display: none;">
              <div></div>
            </div>

            <div ref="rootRef" class="dropdown" :class="{ show: isOpen }">
              <div class="no-action-label" v-if="formElement.isFixed">
                <div class="icon">
                  <div class="iconBox">
                    <LucideIcon :icon="formElement.icon" style="font-size: 18px"/>
                  </div>
                </div>
                <div class="content">{{ labelModel }}</div>
              </div>

              <ButtonElement v-if="!formElement.isFixed" class="btn-label btn-large p-0"
                             id="dropdownMenuButton"
                             :aria-expanded="isOpen"
                             @click="toggle">
                <div class="icon">
                  <div class="iconBox">
                    <LucideIcon :icon="formElement.icon" style="font-size: 18px"/>
                  </div>
                </div>
                <div class="content">{{ labelModel }}</div>
              </ButtonElement>

              <ul v-if="!formElement.isFixed" class="dropdown-menu" :class="{ show: isOpen }" id="dropdownMenuList">
               <div class="dropdown-menu-box animation">
                  <div class="dropdown-menu-head">Property Details</div>
                  <li>
                    <div class="dropdown-item-input">
                      <InputElement v-model="labelModel" type='text' :name="labelValue"
                                    :is-disable="!formElement.isCustom"/>
                    </div>
                  </li>
                  <div class="dropdown-menu-head">Actions</div>
                  <li v-for="(option, index) in propertyActions" :key="index">
                    <a
                      @click="labelDropdownChanged(option)"
                      :id="option.name"
                      v-if="option.name"
                      class="dropdown-item"
                    >
                      <div class="icon">
                        <LucideIcon :icon="option.icon"/>
                      </div>
                      <div class="content">
                        <h6>{{ option.name }}</h6>
                        <p v-if="option.content">{{ option.content }}</p>
                      </div>
                    </a>
                  </li>
                </div>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div :class="!formElement.dropdown ? 'col-8' : 'col-5'">
        <InputElement
          v-if="formElement.input.type === 'text'"
          v-model="inputModel"
          :name="formElement.input.name"
          :type="formElement.input.type"
          :placeholder="formElement.input.placeholder"
          :is-disable="formElement.input.isDisable"
          :is-locked="formElement.input.isLocked"
          :callback-button="formElement.input.callbackButton"
        />

        <TextareaElement
          v-if="formElement.input.type === 'textarea'"
          v-model:value="inputModel"
          type="textarea"
          :name="formElement.input.name"
          :placeholder="formElement.input.placeholder"
          :rows="8"
          :is-disable="formElement.input.isDisable"
        />

        <MultiSelectDropdownElement
          v-if="formElement.input.type === 'multiselect'"
          v-model:value="inputModel"
          :options="formElement.input.selectOptions"
        />

        <InputRangeElement v-if="formElement.input.type === 'range'"
                           v-model:value="inputModel"
                           :type="formElement.input.type"
                           :name="formElement.input.name"
                           :placeholder="formElement.input.placeholder"
                           :is-disable="formElement.input.isDisable"
                           :options="formElement.input.selectOptions"
        />

        <SelectableDropdownElement
          v-if="formElement.input.type === 'select'"
          v-model:value="inputModel"
          :title="formElement.label"
          type="buttonselect"
          :name="formElement.input.name"
          :placeholder="formElement.input.placeholder"
          :is-disable="formElement.input.isDisable"
          :options="formElement.input.selectOptions"
        />
      </div>

    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import {
  ButtonElement,
  InputElement,
  InputRangeElement,
  MultiSelectDropdownElement,
  SelectableDropdownElement,
  TextareaElement,
} from '@shared/components/elements';
import { useDropdown } from '@shared/composables/use-dropdown';

import { CommonNs, type DropdownNs } from '@shared/types';

export default defineComponent({
  name: 'FormProfileGroupSection',
  components: {
    ButtonElement,
    InputElement,
    TextareaElement,
    SelectableDropdownElement,
    MultiSelectDropdownElement,
    InputRangeElement,
  },
  setup() {
    const {isOpen, rootRef, toggle, close} = useDropdown();
    return {isOpen, rootRef, toggle, close};
  },
  emits: ['labelActionSelected', 'update:inputValue', 'update:dropdownValue', 'update:labelValue'],
  props: {
    pKey: {
      type: Number,
      required: false,
    },
    formElement: {
      type: Object as PropType<CommonNs.InputFormGroup>,
      required: false,
    },
    inputValue: {
      type: String,
      default: ''
    },
    labelValue: {
      type: String,
      required: true,
    },
    dropdownValue: {
      type: String,
      required: true,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    inputModel: {
      get(): any {
        return this.inputValue;
      },
      set(inputValue: any): void {
        this.$emit('update:inputValue', inputValue);
      },
    },
    labelModel: {
      get(): any {
        return this.labelValue;
      },
      set(labelValue: any): void {
        this.$emit('update:labelValue', labelValue);
      },
    },
    dropdownModel: {
      get(): string {
        return this.dropdownValue;
      },
      set(dropdownValue: string): void {
        this.$emit('update:dropdownValue', dropdownValue);
      },
    },
  },
  methods: {
    labelDropdownChanged(selectedAction: DropdownNs.DropdownOptionsItem) {
      this.$emit('labelActionSelected', selectedAction.value, this.pKey);
      this.close();
    },

    initPropertyActions(): DropdownNs.DropdownOptionsItem[] {
      const propertyActions: DropdownNs.DropdownOptionsItem[] = [
        {
          name: 'Delete property',
          value: 'DELETE',
          iconType: 'fal',
          icon: 'Trash2',
        },
      ];

      if (this.formElement?.isCustom) {
        propertyActions.splice(0, 0, {
          name: 'Duplicate property',
          value: 'DUPLICATE',
          iconType: 'fal',
          icon: 'Copy',
        });
      }

      return propertyActions;
    },
  },
  data() {
    return {
      propertyActions: this.initPropertyActions(),
      drag: false,
      message: ['vue.draggable'],
    };
  },
});
</script>

<style lang="scss" scoped>
.dropdown {
  width: 100%;
}

.iconBox {
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center
}

.no-action-label {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: var(--fs-14);
  height: 40px;
}

.form-group {
  width: 100%;
  margin-bottom: 15px;

  & .draggable {
    display: flex;
    align-items: center;
    height: 100%;
  }

  & .notdraggable {
    display: flex;
    align-items: center;
    height: 100%;
  }

  & .action-dropdown {
    display: flex;
    align-items: flex-start;
    justify-content: center;

    & > div {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }

  & .label {
    display: flex;
    align-items: center;
    height: 100%;

    & > div {
      display: flex;
      width: 100%;

      & > .icon {
        display: flex;
        align-items: center;
        width: 50px;
      }

      & > .content {
        width: 100%;
        font-size: var(--fs-14);
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}

.description {
  & p {
    font-size: var(--fs-12);
    font-weight: 400;
    color: var(--text-neutral);
    margin: var(--space-0);
  }
}

.draggable-icon {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.notDraggable {
  width: 25px;
  height: 40px;
  margin-right: 5px;

  div {
    width: 25px;
    height: 40px;
    margin-right: 5px;
  }
}

.actions-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: $br-sm;

}
</style>
